import type { Locale } from '@application/Types/Locale'
import { Opt } from '@src/Application/Types/Opt'
import { getCompletePath } from '@src/Application/Utils/StringUtility'
import { ensureTrailingSlash } from '@src/Infrastructure/Url/ensureTrailingSlash'

type Props = {
    lang: Locale
    siteUrl: string
    pageUrl: string
    title: string
    description: string
    contentUrl: string
    telephone: string[]
    organizationName: string
    organizationLogoPath: string
    organizationInfoEmail: string
    siteMetadata: Opt<Record<string, string>>
}

export const getSchema = ({
    lang,
    pageUrl,
    title,
    description,
    contentUrl,
    telephone,
    siteUrl,
    organizationName,
    organizationLogoPath,
    organizationInfoEmail,
    siteMetadata,
}: Props): Record<string, unknown> => {
    const rootUrl = ensureTrailingSlash(`${ensureTrailingSlash(siteUrl)}${lang !== 'en' ? lang : ''}`)

    const sameAs = [siteMetadata?.facebookLink, siteMetadata?.twitterLink, siteMetadata?.googlePlusLink].filter(Boolean)

    const organizationAddress = {
        '@type': 'PostalAddress',
        streetAddress: siteMetadata?.streetAddress,
        addressLocality: siteMetadata?.addressLocality,
        addressRegion: siteMetadata?.addressRegion,
        postalCode: siteMetadata?.postalCode,
        addressCountry: siteMetadata?.addressCountry,
    }

    return {
        '@context': 'http://schema.org',
        '@graph': [
            {
                '@type': 'Organization',
                name: organizationName,
                logo: organizationLogoPath,
                email: organizationInfoEmail,
                url: rootUrl,
                sameAs: sameAs,
            },
            {
                '@type': 'WebSite',
                url: rootUrl,
            },
            {
                '@context': 'http://schema.org',
                '@type': 'WebPage',
                name: title,
                url: pageUrl,
                description,
                primaryImageOfPage: {
                    '@context': 'http://schema.org',
                    '@type': 'ImageObject',
                    contentUrl,
                },
            },
            {
                '@type': 'LocalBusiness',
                name: organizationName,
                image: organizationLogoPath,
                telephone,
                address: {
                    ...organizationAddress,
                },
            },
        ],
    }
}

export const getCmsSchameMarkup = (schemaMarkup: Opt<string>): Opt<string> => {
    try {
        if (schemaMarkup && JSON.parse(schemaMarkup.trim())) {
            return schemaMarkup.trim()
        }
    } catch (e) {
        return undefined
    }
}

export const getOgImagePath = (ogImageUrlPath: Opt<string>): Opt<string> => {
    return ogImageUrlPath ? getCompletePath(ogImageUrlPath) : undefined
}

type GetOgImageUrl = ({
    imageUrl,
    ogImageUrlPath,
    siteUrl,
}: {
    imageUrl: Opt<string>
    ogImageUrlPath: Opt<string>
    siteUrl: string
}) => string
export const getOgImageUrl: GetOgImageUrl = (params): string => {
    const { imageUrl, ogImageUrlPath, siteUrl } = params

    if (imageUrl) return imageUrl

    if (ogImageUrlPath) return `${siteUrl}${ogImageUrlPath}`

    return ''
}

export const getFormattedDateForTitle = (locale: Locale): string => {
    const locales: Record<Locale, string> = {
        en: 'en-US',
        es: 'es-ES',
        ar: 'ar-AE',
        de: 'de-DE',
        nl: 'nl-NL',
    }

    const currentDate = new Date()
    return currentDate.toLocaleDateString(locales[locale], {
        month: 'long',
        year: 'numeric',
    })
}
